import { UNRELEASED_LANGUAGES } from "i18n/unreleased";

/** Languages the certificate image/PDF/page can be rendered in (?lang=). */
export const CERTIFICATE_LANGUAGES = ["en", "es", "de", "fr", "it", "ja", "pt", "ar"] as const;

export type CertificateLang = (typeof CERTIFICATE_LANGUAGES)[number];

/**
 * Subset the language picker offers. Kept in step with the product language
 * picker (i18n/config.ts) so a user can never pick a certificate language the
 * rest of the app cannot switch to — pt/ar joined once i18n shipped pt_br/ar,
 * and a language still in UNRELEASED_LANGUAGES stays out here as well.
 */
export const CERTIFICATE_PICKER_LANGUAGES: readonly CertificateLang[] = CERTIFICATE_LANGUAGES.filter(
    (lang) => !UNRELEASED_LANGUAGES.includes(lang)
);

/**
 * Normalizes a raw ?lang= value to a supported certificate language:
 * lowercases, strips the region suffix ("pt-BR" -> "pt") and falls back to
 * English for anything unsupported.
 */
export function normalizeCertificateLang(raw?: string | null): CertificateLang {
    if (!raw) return "en";
    const base = raw.trim().toLowerCase().split(/[-_]/)[0];
    return (CERTIFICATE_LANGUAGES as readonly string[]).includes(base) ? (base as CertificateLang) : "en";
}

/**
 * Current UI language for certificate rendering, read from the i18next cookie
 * (LANGUAGE_COOKIE in i18n/config.ts — kept in sync by the language detector).
 * Read directly so Effector models and small client components don't need to
 * pull in the i18next instance. Falls back to English on the server.
 */
export function currentCertificateLang(): CertificateLang {
    if (typeof document === "undefined") return "en";
    const match = document.cookie.match(/(?:^|;\s*)i18nextLng=([^;]+)/);
    return normalizeCertificateLang(match ? decodeURIComponent(match[1]) : null);
}
